import type * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

interface CenteredDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 기본 폭은 확인 다이얼로그 규격(w-90). 넓은 모달은 여기서 덮는다 */
  className?: string;
  children: React.ReactNode;
}

/**
 * 화면 중앙에 뜨는 작은 다이얼로그의 껍데기 — 오버레이·위치·라운드·그림자·전환.
 *
 * 확인 다이얼로그와 등록 완료 안내처럼 본문 구성만 다른 모달들이 같은 껍데기를 쓴다.
 * 클래스 목록을 각 파일에 복사하면 한쪽만 바뀌어 서로 어긋난다.
 *
 * `Title`·`Description` 은 담지 않는다 — Radix 가 접근성 경고를 내므로 본문에서 반드시
 * 하나씩 렌더해야 한다.
 */
export function CenteredDialog({
  open,
  onOpenChange,
  className,
  children,
}: CenteredDialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-bg-dim",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-90",
            "-translate-x-1/2 -translate-y-1/2",
            "bg-bg-primary rounded-2xl overflow-hidden",
            "inset-border-normal",
            "shadow-[0px_0px_80px_0px_rgba(0,0,0,0.25)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "outline-none",
            className,
          )}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

CenteredDialog.displayName = "CenteredDialog";
